import type { SvgIconComponent } from "@mui/icons-material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { blue, cyan, deepOrange, green, indigo, orange, purple, teal } from "@mui/material/colors";

/**
 * The colour and icon of each permission area, keyed by the server's `PermissionAreas.Names`.
 *
 * One module, so an area looks the same on every permissions page: a stripe and an icon on the
 * area header, never tinted fills and coloured borders on every card. The names are a wire
 * contract - the permissions page sends them as `?categories=` and `GET /permissions/kinds` returns
 * them - so they are written here exactly as the server spells them. A test reads
 * `PermissionAreas.cs` and fails on an area with no token here, which is how Calendar went missing
 * from the old category list without anybody noticing.
 *
 * The colours are the ones the old category list used, so an area keeps its colour across the
 * redesign; Calendar, which the old list never had, takes indigo.
 */
export type PermissionAreaToken = Readonly<{
  color: string;
  Icon: SvgIconComponent;
}>;

export const PERMISSION_AREA_TOKENS = {
  Account: { color: teal[800], Icon: AccountCircleOutlinedIcon },
  Calendar: { color: indigo[800], Icon: CalendarMonthOutlinedIcon },
  Communication: { color: cyan[800], Icon: ChatBubbleOutlineOutlinedIcon },
  Documents: { color: deepOrange[800], Icon: DescriptionOutlinedIcon },
  Financial: { color: green[800], Icon: AttachMoneyOutlinedIcon },
  "Knowledge Base": { color: blue[800], Icon: MenuBookOutlinedIcon },
  Marketplace: { color: purple[800], Icon: StorefrontOutlinedIcon },
  Work: { color: orange[800], Icon: WorkOutlineOutlinedIcon },
} as const satisfies Record<string, PermissionAreaToken>;

export type PermissionAreaName = keyof typeof PERMISSION_AREA_TOKENS;

const NEUTRAL_AREA_TOKEN: PermissionAreaToken = {
  color: "text.secondary",
  Icon: DescriptionOutlinedIcon,
};

/**
 * The token for `area`. An area the server added before this module caught up draws neutral
 * rather than failing the page: the grant is still a fact somebody holds, and the test above is
 * where the gap is meant to be caught.
 */
export function permissionAreaToken(area: string): PermissionAreaToken {
  return Object.hasOwn(PERMISSION_AREA_TOKENS, area)
    ? PERMISSION_AREA_TOKENS[area as PermissionAreaName]
    : NEUTRAL_AREA_TOKEN;
}
